import {
  getActiveUser,
  navigate,
  sendGetRequest,
  getUserRole,
  getUserStatus,
  sendPatchRequest,
  sendDeleteRequest,
  sendPostRequest,
  formatDate,
  allowedOperations,
} from "../utils/utils";
import DataListWithSearch from "./component/@list/list-search";
import { useState, useEffect } from "react";
import AppRoutes from "../routing/routes";
import { coreStyles } from "../theme/style";
import { Box, Button, ButtonGroup, Icon } from "@mui/material";

const getTableHead = () => {
  return [
    { id: "createdAt", label: "Created On", alignRight: false },
    { id: "firstName", label: "Full Name", alignRight: false },
    { id: "email", label: "Email Address", alignRight: false },
    { id: "role", label: "Role", alignRight: false },
    { id: "lastSeenAt", label: "Last Seen", alignRight: false },
    { id: "access", label: "Permissions", alignRight: false },
    { id: "active", label: "Status", alignRight: false },
  ];
};

const btnGroupOptions = [
  {
    label: "Partners",
    destination: `${AppRoutes.users}/${AppRoutes.partners}`,
  },
];

const startEdit = (uuid) =>
  navigate(
    `${AppRoutes.users}/${AppRoutes.user}${uuid ? `?uuid=${uuid}` : ""}`,
    true
  );

const UserList = () => {
  const loggedUser = getActiveUser();
  const [dataList, setDataList] = useState([]);
  const [loading, onLoading] = useState(false);
  const [filterKey, setFilterkey] = useState("firstName");
  const appStyle = coreStyles();

  const { canEdit, canDelete, canCreate } = allowedOperations();

  const userOptions = [
    canDelete ? {
      icon: "delete",
      value: "delete",
      label: "Delete",
      onClick: (value, selected) => {
        onLoading(true);
        Promise.all(
          selected.map((uuid) => sendDeleteRequest("users", uuid))
        ).then((response) => {
          executeFetch();
        });
      },
    }: null,
    canEdit ? {
      icon: "edit",
      value: "edit",
      label: "Edit",
      onClick: (_value, selected) => startEdit(selected),
    }: null,
    canCreate ? {
      icon: "toggle_on",
      value: "activate",
      label: "Activate",
      onClick: (_value, selected) => executeUpdate(true, selected),
    }: null,
    canCreate ? {
      icon: "toggle_off",
      value: "de-activate",
      label: "Deactivate",
      onClick: (_value, selected) => executeUpdate(false, selected),
    }: null,
    canCreate ? {
      icon: "password",
      value: "reset-password",
      label: "Reset Password",
      onClick: (_value, selected) => {
        sendPostRequest("auth/reset-user-password", { uuid: selected[0] });
      },
    }: null,
  ].filter(Boolean);
  const filterOptions = [
    { value: "firstName", label: "Full name", onClick: setFilterkey },
    { value: "email", label: "Email Address", onClick: setFilterkey },
  ];

  const executeFetch = () => {
    sendGetRequest(
      "users/admins",
      (loading) => onLoading(loading),
      (data) =>
        setDataList(data.filter((user) => user.email !== loggedUser.email))
    );
  };

  useEffect(
    () => executeFetch(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loggedUser.email]
  );

  const executeUpdate = (active, uuids) => {
    onLoading(true);
    Promise.all(
      uuids.map((uuid) => sendPatchRequest(`users/${uuid}`, { active }))
    ).then((res) => {
      executeFetch();
      onLoading(false);
    });
  };

  return (
    <div>
      <DataListWithSearch
        sectionTitle="Users"
        createNewElement={
          canCreate ? (<Box sx={{ display: "flex", flexDirection: "row" }}>
            <ButtonGroup size="large" aria-label="large button group">
              {btnGroupOptions.map((btn) => {
                return (
                  <Button
                    key={btn.destination}
                    onClick={() => navigate(`${btn.destination}`, true)}
                  >
                    {btn.label}
                  </Button>
                );
              })}
            </ButtonGroup>
            <Button
              variant="contained"
              onClick={() => startEdit()}
              className={appStyle.actionButonCustom}
              startIcon={
                <Icon children={"add"} className={appStyle.actionButonIcon} />
              }
            >
              {loggedUser.active && canCreate ? "Add User" : null}
            </Button>
          </Box>): null
        }
        data={dataList}
        primaryKey="uuid"
        filterColumn={filterKey}
        emptyIcon="groups"
        avatarKey="uuid"
        loading={loading}
        onCreateNewItem={() => startEdit()}
        headers={getTableHead()}
        rowData={(data) => {
          const _entries = [
            { value: formatDate(data.createdAt, "MMM DD, YYYY") },
            {
              value: `${data.firstName} ${data.lastName}`,
              width: 160,
            },
            { value: data.email },
            { value: getUserRole(parseInt(`${data.role}`)) },
            { value: formatDate(data.lastSeenAt, "MMM DD, YYYY hh:mm A") },
            {
              value: `${(data.permissions || []).length} Access, ${
                (data.actions || []).length
              } Actions`,
            },
            { ...getUserStatus(data.active) },
          ];
          return {
            exclude: [
              data.active ? "activate" : "deactivate",
              loggedUser.role !== 1 ? "reset-password" : "",
            ],
            entries: _entries,
          };
        }}
        selectionOptions={userOptions.filter(
          (option) => option.value !== "edit"
        )}
        filterOptions={filterOptions}
        optionItems={userOptions}
      />
    </div>
  );
};

export default UserList;
